/* eslint-disable @next/next/no-img-element */
import { FC } from 'react';

import { TColorProp, useTheme } from 'styled-components';

import { getThemeColor } from '~/common/utils/get-theme-color';

import { IBadgeProps, TBadgeColor, TBadgeVariant } from './types';
import { Container, DotWrapper } from './styles';
import { Typography } from '../Typography';
import { Icon } from '../Icons';
import { Tooltip } from '../Above/Tooltip';

const getBaseColorProp = {
  blue: 'blue.500',
  green: 'green.600',
  grey: 'grey.500',
  orange: 'orange.600',
  red: 'red.600',
  yellow: 'yellow.600',
  purple: 'purple.500',
  sky: 'sky.600',
  pink: 'pink.600',
  teal: 'teal.600',
  neutral: 'neutral.200',
} as Record<TBadgeColor, TColorProp>;

const getTintColorProp = (
  color: TBadgeColor,
  variant: TBadgeVariant,
  isDisabled: boolean
): TColorProp => {
  if (isDisabled) return 'grey.600';

  if (variant === 'filled') return 'grey.0';

  if (variant === 'light') {
    const getLightColor = {
      blue: 'blue.400',
      green: 'green.400',
      grey: 'grey.300',
      orange: 'orange.400',
      red: 'red.400',
      yellow: 'yellow.400',
      purple: 'purple.400',
      sky: 'sky.400',
      pink: 'pink.400',
      teal: 'teal.400',
      neutral: 'neutral.0',
    } as Record<TBadgeColor, TColorProp>;
    return getLightColor[color];
  }

  return getBaseColorProp[color];
};

const getBackgroundColorProp = (
  color: TBadgeColor,
  variant: TBadgeVariant,
  isDisabled: boolean
): TColorProp => {
  if (isDisabled || variant === 'outlined') return 'transparent';

  if (variant === 'filled') return getBaseColorProp[color];

  if (variant === 'light') {
    const getLightColor = {
      blue: 'alphaBlue.24',
      green: 'alphaGreen.16',
      grey: 'alphaGrey.24',
      orange: 'alphaOrange.24',
      red: 'alphaRed.24',
      yellow: 'alphaYellow.24',
      purple: 'alphaPurple.24',
      sky: 'alphaSky.24',
      pink: 'alphaPink.24',
      teal: 'alphaTeal.24',
    } as Record<TBadgeColor, TColorProp>;
    return getLightColor[color];
  }

  if (variant === 'lighter') {
    const getLighterColor = {
      blue: 'alphaBlue.16',
      green: 'alphaGreen.10',
      grey: 'alphaGrey.16',
      orange: 'alphaOrange.16',
      red: 'alphaRed.16',
      yellow: 'alphaYellow.16',
      purple: 'alphaPurple.16',
      sky: 'alphaSky.16',
      pink: 'alphaPink.16',
      teal: 'alphaTeal.16',
    } as Record<TBadgeColor, TColorProp>;
    return getLighterColor[color];
  }
};

export const Badge: FC<IBadgeProps> = ({
  color,
  number,
  variant = 'filled',
  iconPosition = 'right',
  icon,
  withLeftDot = false,
  hint,
  text = '',
  size = 'medium',
  isDisabled = false,
  leftDotColor: _leftDotColor,
  textColor: _textColor,
  borderColor: _borderColor,
  fixedWidth,
  cursor = 'default',
  imageUrl,
  iconColor,
  style,
}) => {
  const theme = useTheme();

  const isNumber = typeof number === 'number';

  const tintColor: TColorProp = getTintColorProp(color, variant, isDisabled);
  const backgroundColor: TColorProp = getBackgroundColorProp(
    color,
    variant,
    isDisabled
  );

  const _iconColor = getThemeColor(theme, tintColor);
  const iconSize = size === 'large' ? 20 : size === 'medium' ? 16 : 12;
  const shouldRenderIcon = !!icon && !withLeftDot;

  const leftDotColor = _leftDotColor || tintColor;
  const textColor = _textColor || tintColor;
  const borderColor = _borderColor || tintColor;

  const Element = (
    <Container
      style={style}
      $size={size}
      $variant={variant}
      $backgroundColor={backgroundColor}
      $isDisabled={isDisabled}
      $isNumber={isNumber}
      $shouldRenderIcon={shouldRenderIcon}
      $iconPosition={iconPosition}
      $tintColor={tintColor}
      $withLeftDot={withLeftDot}
      $number={number}
      $borderColor={borderColor}
      $cursor={cursor}
      $fixedWidth={fixedWidth}
    >
      {!!withLeftDot && (
        <DotWrapper $color={leftDotColor} $size={size}>
          <span />
        </DotWrapper>
      )}

      {shouldRenderIcon && iconPosition === 'left' && (
        <Icon type={icon} color={iconColor || _iconColor} size={iconSize} />
      )}

      {imageUrl && (
        <img
          style={{ borderRadius: '50%' }}
          src={imageUrl}
          alt={text || 'badge'}
        />
      )}

      {size === 'small' ? (
        <Typography.Subheading size="2x-small" color={textColor}>
          {isNumber ? number : text}
        </Typography.Subheading>
      ) : size === 'medium' ? (
        <Typography.Label size="x-small" color={textColor}>
          {isNumber ? number : text}
        </Typography.Label>
      ) : (
        <Typography.Label size="small" color={textColor}>
          {isNumber ? number : text}
        </Typography.Label>
      )}

      {shouldRenderIcon && iconPosition === 'right' && (
        <Icon type={icon} color={iconColor || _iconColor} size={iconSize} />
      )}
    </Container>
  );

  const WithOrWithoutTooltip = hint ? (
    <Tooltip.Simple message={hint}>{Element}</Tooltip.Simple>
  ) : (
    <>{Element}</>
  );

  return <>{WithOrWithoutTooltip}</>;
};
